import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Socket, Channel } from 'phoenix';
import { useUserStore } from '@/store/useUserStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';

interface WebSocketContextType {
  socket: Socket | null;
  channel: Channel | null;
  isConnected: boolean;
  isJoined: boolean;
  lastEventTime: number | null;
  eventCount: number;
}

const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  channel: null,
  isConnected: false,
  isJoined: false,
  lastEventTime: null,
  eventCount: 0,
});

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [lastEventTime, setLastEventTime] = useState<number | null>(null);
  const [eventCount, setEventCount] = useState(0);
  const socketRef = useRef<Socket | null>(null);
  const channelRef = useRef<Channel | null>(null);
  
  const { session } = useUserStore();
  const { selectedOrganizationId } = useOrganizationStore();
  
  const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:4000/socket';
  
  useEffect(() => {
    if (!session?.access_token || !selectedOrganizationId) {
      // Cleanup if no auth or organization
      if (channelRef.current) {
        channelRef.current.leave();
        channelRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      setIsJoined(false);
      return;
    }

    // Create socket connection
    const socket = new Socket(wsUrl, {
      params: {
        token: session.access_token
      }
    });

    socketRef.current = socket;

    // Socket event handlers
    socket.onOpen(() => {
      setIsConnected(true);
      console.log('WebSocket connected');
    });

    socket.onClose(() => {
      setIsConnected(false);
      setIsJoined(false);
      console.log('WebSocket disconnected');
    });

    socket.onError((error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
      setIsJoined(false);
    });

    // Connect the socket
    socket.connect();

    // Join organization channel
    const channelTopic = `org:${selectedOrganizationId}`;
    const channel = socket.channel(channelTopic, {});
    channelRef.current = channel;

    channel.join()
      .receive('ok', (response) => {
        setIsJoined(true);
        console.log(`Joined channel ${channelTopic}`, response);
      })
      .receive('error', (response) => {
        setIsJoined(false);
        console.error(`Failed to join channel ${channelTopic}`, response);
      })
      .receive('timeout', () => {
        setIsJoined(false);
        console.error(`Timeout joining channel ${channelTopic}`);
      });

    channel.onError(() => {
      console.error(`Channel ${channelTopic} error`);
      setIsJoined(false);
    });

    channel.onClose(() => {
      console.log(`Channel ${channelTopic} closed`);
      setIsJoined(false);
    });

    // Listen for any event on the channel to track activity
    channel.on('incoming_request', () => {
      setLastEventTime(Date.now());
      setEventCount(prev => prev + 1);
    });
    
    channel.on('webhook_delivery', () => {
      setLastEventTime(Date.now());
      setEventCount(prev => prev + 1);
    });
    
    channel.on('webhook_attempt', () => {
      setLastEventTime(Date.now());
      setEventCount(prev => prev + 1);
    });

    // Cleanup
    return () => {
      if (channel) {
        channel.leave();
      }
      if (socket) {
        socket.disconnect();
      }
    };
  }, [session?.access_token, selectedOrganizationId, wsUrl]);

  return (
    <WebSocketContext.Provider 
      value={{ 
        socket: socketRef.current, 
        channel: channelRef.current, 
        isConnected, 
        isJoined,
        lastEventTime,
        eventCount
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}